import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, Users, Briefcase, UserRound, RefreshCw, ShieldOff, ShieldCheck, Layers, Gem, ArrowUpCircle, Check, Linkedin, Copy } from "lucide-react";
import { RegionalImagerySettings } from "@/components/RegionalImagerySettings";
import { OrgBrandingEditor } from "@/components/admin/OrgBrandingEditor";
import { Switch } from "@/components/ui/switch";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const PLAN_COLORS = { trial: "#64748b", starter: "#10B981", growth: "#6366f1", enterprise: "#f59e0b" };

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const lim = (n) => (!n || n <= 0 ? "∞" : n);
const PRESETS = { trial: { max_seats: 2, max_jobs: 3 }, starter: { max_seats: 5, max_jobs: 15 }, growth: { max_seats: 20, max_jobs: 60 }, enterprise: { max_seats: 0, max_jobs: 0 } };

function Stat({ icon: Icon, label, value, tone = "text-foreground" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`text-2xl font-display font-bold ${tone}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function SuperAdmin() {
  const [overview, setOverview] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null);
  const [planTarget, setPlanTarget] = useState(null);
  const [planForm, setPlanForm] = useState({ plan: "trial", max_seats: 2, max_jobs: 3 });
  const [upgrades, setUpgrades] = useState([]);
  const [linkedin, setLinkedin] = useState(null);
  const [liForm, setLiForm] = useState({ client_id: "", client_secret: "" });
  const [liBusy, setLiBusy] = useState(false);

  const openPlan = (o) => {
    setPlanForm({ plan: o.plan || "trial", max_seats: o.max_seats ?? 0, max_jobs: o.max_jobs ?? 0 });
    setPlanTarget(o);
  };
  const pickPlan = (p) => setPlanForm((f) => ({ ...f, plan: p, ...(PRESETS[p] || {}) }));
  const savePlan = async () => {
    try {
      await api.patch(`/superadmin/orgs/${planTarget.id}/plan`, {
        plan: planForm.plan,
        max_seats: Number(planForm.max_seats) || 0,
        max_jobs: Number(planForm.max_jobs) || 0,
      });
      toast.success(`${planTarget.name} moved to ${planForm.plan}`);
      setPlanTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update plan"); }
  };

  const load = () => {
    setLoading(true); setErr(null);
    Promise.all([api.get("/superadmin/overview"), api.get("/superadmin/orgs"), api.get("/superadmin/upgrade-requests")])
      .then(([o, g, u]) => { setOverview(o.data); setOrgs(g.data); setUpgrades(u.data || []); })
      .catch((e) => setErr(e.message)).finally(() => setLoading(false));
    api.get("/superadmin/integrations/linkedin").then(({ data }) => { setLinkedin(data); setLiForm({ client_id: data.client_id || "", client_secret: "" }); }).catch(() => setLinkedin(null));
  };
  useEffect(load, []);

  const saveLinkedIn = async (patch) => {
    setLiBusy(true);
    try {
      const body = patch || { client_id: liForm.client_id.trim(), ...(liForm.client_secret.trim() ? { client_secret: liForm.client_secret.trim() } : {}) };
      const { data } = await api.put("/superadmin/integrations/linkedin", body);
      setLinkedin(data); setLiForm((f) => ({ client_id: data.client_id || "", client_secret: "" }));
      toast.success("LinkedIn integration updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setLiBusy(false); }
  };

  const resolveUpgrade = async (req) => {
    try { await api.patch(`/superadmin/upgrade-requests/${req.id}`); toast.success(`${req.org_name} request resolved`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resolve request"); }
  };

  const toggle = async (org) => {
    const action = org.status === "suspended" ? "reactivate" : "suspend";
    setBusy(org.id);
    try {
      await api.post(`/superadmin/orgs/${org.id}/${action}`);
      toast.success(`${org.name} ${action === "suspend" ? "suspended" : "reactivated"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update organization"); }
    finally { setBusy(null); }
  };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/superadmin/orgs/${id}`); setDetail(data); }
    catch { toast.error("Could not load organization"); }
  };

  if (loading) return <LoadingState label="Loading platform console…" />;
  if (err) return <div className="p-8"><ErrorState message={err} onRetry={load} /></div>;

  const PLANS = ["trial", "starter", "growth", "enterprise"];
  const planData = PLANS.map((p) => {
    const rows = orgs.filter((o) => (o.plan || "trial") === p);
    const used = rows.reduce((s, o) => s + (o.seats_used || 0), 0);
    const limit = rows.reduce((s, o) => s + (o.max_seats && o.max_seats > 0 ? o.max_seats : 0), 0);
    const unlimited = rows.some((o) => !o.max_seats || o.max_seats <= 0);
    return { plan: p, label: p[0].toUpperCase() + p.slice(1), orgs: rows.length, used, limit, unlimited };
  }).filter((d) => d.orgs > 0);
  const totalSeatsUsed = orgs.reduce((s, o) => s + (o.seats_used || 0), 0);

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-6xl mx-auto overflow-x-hidden" data-testid="superadmin-console">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Platform · all organizations</div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Organizations</h1>
          <p className="text-sm text-muted-foreground mt-1">Every tenant on HireStream, fully isolated. Suspend to instantly cut off access and hide their live roles.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="superadmin-refresh" className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="superadmin-overview">
        <Stat icon={Building2} label="Organizations" value={overview?.orgs} />
        <Stat icon={ShieldCheck} label="Active" value={overview?.active} tone="text-emerald-400" />
        <Stat icon={ShieldOff} label="Suspended" value={overview?.suspended} tone={overview?.suspended ? "text-rose-400" : "text-foreground"} />
        <Stat icon={Users} label="Total users" value={overview?.users} />
        <Stat icon={UserRound} label="Recruiters" value={overview?.recruiters} />
        <Stat icon={UserRound} label="Candidates" value={overview?.candidates_users} />
        <Stat icon={Briefcase} label="Jobs" value={overview?.jobs} />
        <Stat icon={Layers} label="Published roles" value={overview?.published_jobs} />
      </div>

      {overview?.by_plan && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="plan-breakdown">
          <span className="uppercase tracking-wider text-muted-foreground">Plans:</span>
          {["trial", "starter", "growth", "enterprise"].filter((p) => overview.by_plan[p]).map((p) => (
            <span key={p} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 capitalize">
              <Gem className="h-3 w-3 text-primary" />{p}<span className="font-semibold text-foreground">{overview.by_plan[p]}</span>
            </span>
          ))}
        </div>
      )}

      <RegionalImagerySettings />
      {linkedin && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="linkedin-integration">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold"><Linkedin className="h-4 w-4 text-[#0a66c2]" /> LinkedIn sign-in</div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${linkedin.active ? "bg-emerald-500/15 text-emerald-400" : "bg-muted text-muted-foreground"}`} data-testid="linkedin-status">{linkedin.active ? "Active" : "Not configured"}</span>
              <Switch checked={linkedin.enabled} onCheckedChange={(v) => saveLinkedIn({ enabled: v })} disabled={liBusy} data-testid="linkedin-enabled-toggle" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Configure keys here — no code or env edits needed. Add this exact redirect URL in your LinkedIn app's Auth settings:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate text-xs bg-background/60 border border-border rounded-lg px-3 py-2 font-mono" data-testid="linkedin-redirect">{linkedin.redirect_uri}</code>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(linkedin.redirect_uri); toast.success("Redirect URL copied"); }} data-testid="linkedin-redirect-copy" className="gap-1.5 shrink-0"><Copy className="h-3.5 w-3.5" /> Copy</Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Client ID</label>
              <Input value={liForm.client_id} onChange={(e) => setLiForm((f) => ({ ...f, client_id: e.target.value }))} placeholder="86xxxxxxxxxxxx" data-testid="linkedin-client-id" className="font-mono text-xs" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Client Secret {linkedin.has_secret && <span className="text-emerald-400">· saved</span>}</label>
              <Input type="password" value={liForm.client_secret} onChange={(e) => setLiForm((f) => ({ ...f, client_secret: e.target.value }))} placeholder={linkedin.has_secret ? "•••••••• (leave blank to keep)" : "your client secret"} data-testid="linkedin-client-secret" className="font-mono text-xs" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={() => saveLinkedIn()} disabled={liBusy || (!liForm.client_id.trim() && !liForm.client_secret.trim())} data-testid="linkedin-save">Save keys</Button>
          </div>
        </div>
      )}

      {planData.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="plan-analytics">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Organizations by tier</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={planData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="orgs" name="Organizations" radius={[6, 6, 0, 0]} maxBarSize={56}>
                  {planData.map((d) => <Cell key={d.plan} fill={PLAN_COLORS[d.plan]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Seat utilization by tier</div>
              <div className="text-xs text-muted-foreground">{totalSeatsUsed} seats in use</div>
            </div>
            <div className="space-y-3">
              {planData.map((d) => {
                const pct = d.limit > 0 ? Math.min(100, Math.round((d.used / d.limit) * 100)) : 0;
                return (
                  <div key={d.plan} data-testid={`seat-util-${d.plan}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="capitalize flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: PLAN_COLORS[d.plan] }} /> {d.label}</span>
                      <span className="font-mono text-muted-foreground">{d.used}{d.unlimited ? " / ∞" : ` / ${d.limit}`}{!d.unlimited && d.limit > 0 ? ` · ${pct}%` : ""}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: d.unlimited ? "100%" : `${pct}%`, background: PLAN_COLORS[d.plan], opacity: d.unlimited ? 0.35 : 1 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {upgrades.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 space-y-3" data-testid="upgrade-requests">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-300"><ArrowUpCircle className="h-4 w-4" /> Upgrade requests <span className="text-xs font-normal text-amber-300/70">({upgrades.length})</span></div>
          {upgrades.map((u) => (
            <div key={u.id} className="flex flex-col md:flex-row md:items-center gap-3 rounded-lg border border-border bg-card p-3" data-testid={`upgrade-req-${u.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{u.org_name} <span className="text-xs text-muted-foreground capitalize">· on {u.current_plan || "trial"}</span></div>
                <div className="text-xs text-muted-foreground">{u.requested_by_name} ({u.requested_by_email})</div>
                {u.message && <div className="text-xs text-foreground/80 mt-1 italic">“{u.message}”</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => { const o = orgs.find((x) => x.id === u.tenant_id); if (o) openPlan(o); else toast.error("Org not found"); }} data-testid={`upgrade-plan-${u.id}`}><Gem className="h-3.5 w-3.5 mr-1" /> Change plan</Button>
                <Button size="sm" onClick={() => resolveUpgrade(u)} className="gap-1.5" data-testid={`upgrade-resolve-${u.id}`}><Check className="h-3.5 w-3.5" /> Mark resolved</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {orgs.map((o) => (
          <div key={o.id} data-testid={`org-row-${o.id}`}
            className="rounded-xl border border-border bg-card p-4 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/30 to-indigo-500/30 flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-primary" /></div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{o.name}</span>
                  {o.status === "suspended"
                    ? <Badge variant="destructive" data-testid={`org-status-${o.id}`}>Suspended</Badge>
                    : <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30" data-testid={`org-status-${o.id}`}>Active</Badge>}
                  {o.id === "acme" && <Badge variant="outline" className="text-[10px]">Primary</Badge>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="inline-flex items-center gap-1 capitalize text-primary"><Gem className="h-3 w-3" />{o.plan || "trial"}</span>
                  <span>· Seats {o.seats_used ?? 0}/{lim(o.max_seats)}</span>
                  <span>· Roles {o.jobs}/{lim(o.max_jobs)}</span>
                  <span>· {fmtDate(o.created_at)}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm min-w-0">
              <div className="text-center"><div className="font-semibold">{o.jobs}</div><div className="text-[11px] text-muted-foreground">Jobs</div></div>
              <div className="text-center"><div className="font-semibold">{o.published_jobs}</div><div className="text-[11px] text-muted-foreground">Live</div></div>
              <div className="text-center"><div className="font-semibold">{o.users}</div><div className="text-[11px] text-muted-foreground">Users</div></div>
              <div className="text-center"><div className="font-semibold">{o.candidates}</div><div className="text-[11px] text-muted-foreground">Candidates</div></div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => openPlan(o)} data-testid={`org-plan-${o.id}`}>Plan</Button>
              <Button variant="outline" size="sm" onClick={() => openDetail(o.id)} data-testid={`org-view-${o.id}`}>View</Button>
              {o.id === "acme"
                ? <Button variant="ghost" size="sm" disabled title="The primary organization cannot be suspended">Protected</Button>
                : o.status === "suspended"
                  ? <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={busy === o.id} onClick={() => toggle(o)} data-testid={`org-reactivate-${o.id}`}><ShieldCheck className="h-3.5 w-3.5" /> Reactivate</Button>
                  : <Button variant="destructive" size="sm" className="gap-1.5" disabled={busy === o.id} onClick={() => toggle(o)} data-testid={`org-suspend-${o.id}`}><ShieldOff className="h-3.5 w-3.5" /> Suspend</Button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="org-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle><DialogDescription className="sr-only">Organization details, members and controls.</DialogDescription></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Jobs", detail.jobs], ["Live", detail.published_jobs], ["Users", detail.users], ["Candidates", detail.candidates]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-border bg-card p-2"><div className="font-semibold">{v}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>
                ))}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Members</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {(detail.members || []).map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-sm">
                      <span className="truncate">{m.name} <span className="text-muted-foreground">· {m.email}</span></span>
                      <Badge variant="outline" className="capitalize text-[10px] ml-2">{m.org_role || m.role}</Badge>
                    </div>
                  ))}
                  {(!detail.members || detail.members.length === 0) && <div className="text-sm text-muted-foreground">No members yet.</div>}
                </div>
              </div>
              <OrgBrandingEditor orgId={detail.id} orgName={detail.name} />
              {detail.recent_audit?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recent activity</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {detail.recent_audit.map((a) => (
                      <div key={a.id} className="text-xs text-muted-foreground truncate">{a.action} · {a.target}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!planTarget} onOpenChange={(v) => !v && setPlanTarget(null)}>
        <DialogContent className="max-w-md" data-testid="org-plan-dialog">
          <DialogHeader><DialogTitle>{planTarget?.name} — plan & limits</DialogTitle><DialogDescription className="sr-only">Adjust this organization's plan tier and seat and role limits.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Plan</label>
              <Select value={planForm.plan} onValueChange={pickPlan}>
                <SelectTrigger data-testid="plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial — 2 seats · 3 roles</SelectItem>
                  <SelectItem value="starter">Starter — 5 seats · 15 roles</SelectItem>
                  <SelectItem value="growth">Growth — 20 seats · 60 roles</SelectItem>
                  <SelectItem value="enterprise">Enterprise — unlimited</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Seat limit</label>
                <Input type="number" min="0" value={planForm.max_seats} onChange={(e) => setPlanForm((f) => ({ ...f, max_seats: e.target.value }))} data-testid="plan-seats" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Role limit</label>
                <Input type="number" min="0" value={planForm.max_jobs} onChange={(e) => setPlanForm((f) => ({ ...f, max_jobs: e.target.value }))} data-testid="plan-jobs" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Use 0 for unlimited. Seats count recruiters/admins in the org; role limit caps how many jobs the org can create.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanTarget(null)} data-testid="plan-cancel">Cancel</Button>
            <Button onClick={savePlan} data-testid="plan-save">Save plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}